import React, { useEffect, useState } from "react";

import { useParams, useSearchParams } from "react-router-dom";

import { MessageDescriptor } from "@lingui/core";
import { msg, t } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import LoadSpinner from "@readai/common/components/LoadSpinner";
import usePageTitle from "@readai/common/hooks/usePageTitle";
import useWindowDimensions from "@readai/common/hooks/useWindowDimensions";
import Stack from "@readai/pulse/components/Stack";

import usePublicRssLinks from "@app/booking/hooks/usePublicRssLinks";

import ActivateLinkModal from "./ActivateLinkModal";
import ScheduleEventContent from "./ScheduleEventContent";
import { ContentCard, ContentContainer, ContentWrapper } from "./styles";
import UserPanelContent from "./UserPanelContent";

const WIDTH_BREAKPOINT = 1200;
const HEIGHT_BREAKPOINT = 500;

type StepId = "date-and-time" | "details" | "duration";

const STEP_TO_TITLE: {
  [_k in StepId]: { title: MessageDescriptor; untranslatedText: string };
} = {
  details: { title: msg`Details`, untranslatedText: "Details" },
  "date-and-time": {
    title: msg`Date and Time`,
    untranslatedText: "Date and Time",
  },
  duration: { title: msg`Duration`, untranslatedText: "Duration" },
};

const ScheduleEvent = () => {
  const { _ } = useLingui();
  const [queryParams] = useSearchParams();
  const { ownerVanityId, linkId } = useParams();

  const currentStep = queryParams.get("step") as StepId | null;

  const titlePrefix = t`Schedule Event`;
  let currentStepTitle = "";
  let currentStepText = "";

  if (currentStep && STEP_TO_TITLE[currentStep]) {
    const { title, untranslatedText: text } = STEP_TO_TITLE[currentStep];

    currentStepTitle = _(title);
    currentStepText = text;
  }

  usePageTitle(`${titlePrefix}- ${currentStepTitle}`, {
    eventTitle: `Schedule Event- ${currentStepText}`,
  });

  const { windowWidth, windowHeight } = useWindowDimensions();
  const { isDisabled, isLoading, allLinks, ownerName } = usePublicRssLinks(
    ownerVanityId,
    linkId,
  );

  const inMobileView = windowWidth < WIDTH_BREAKPOINT;
  const inShortView = windowHeight < HEIGHT_BREAKPOINT;

  const [expanded, setExpanded] = useState(!inMobileView && !inShortView); // default expanded on larger screens

  const shouldForceExpand = !inMobileView && !expanded;
  const shouldForceCollapse = inShortView && expanded;

  useEffect(() => {
    if (shouldForceExpand) {
      setExpanded(true);
    } else if (shouldForceCollapse) {
      setExpanded(false);
    }
  }, [shouldForceExpand, shouldForceCollapse]);

  if (isLoading && !inMobileView) {
    return (
      <ContentWrapper variant="center">
        <ContentCard variant="center">
          <LoadSpinner />
        </ContentCard>
      </ContentWrapper>
    );
  }

  const handleToggleExpand = () => setExpanded((e) => !e);

  return (
    <ContentWrapper variant="center">
      <ContentCard>
        <UserPanelContent
          expanded={expanded}
          expandable={inMobileView && !inShortView}
          isLoading={isLoading}
          onToggleExpand={handleToggleExpand}
          ownerName={ownerName}
        />
        <ContentContainer>
          {isLoading ? (
            <Stack variant="center" flex={1}>
              <LoadSpinner delay={1000} />
            </Stack>
          ) : (
            <ScheduleEventContent
              allLinks={allLinks}
              autoAdvance={inMobileView}
              ownerName={ownerName}
              ownerVanityId={ownerVanityId}
              linkId={linkId}
            />
          )}
        </ContentContainer>
      </ContentCard>
      {isDisabled && <ActivateLinkModal />}
    </ContentWrapper>
  );
};

export default ScheduleEvent;
